import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import roleService from "../../../Services/roleService";
import staffService from "../../../Services/staffService";

const SectionLabel = ({ children }) => (
  <p className="text-[11px] uppercase tracking-widest font-[500] text-gray-400 mb-3 mt-6">
    {children}
  </p>
);

const InputField = ({ label, hint, ...props }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[12px] font-[500] text-gray-500">{label}</label>
    <input
      {...props}
      className="w-full px-3 py-2 text-[13px] text-gray-800 bg-white border border-gray-200 rounded-lg outline-none hover:border-orange-200 focus:border-orange-200 focus:ring-2 focus:ring-orange-100"
    />
    {hint && <span className="text-[11px] text-gray-400">{hint}</span>}
  </div>
);

const SelectField = ({ label, name, value, onChange, required, children }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[12px] font-[500] text-gray-500">{label}</label>
    <select
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      className="w-full px-3 py-2 text-[13px] text-gray-800 bg-white border border-gray-200 rounded-lg outline-none hover:border-orange-200 focus:border-orange-200 focus:ring-2 focus:ring-orange-100 appearance-none"
    >
      <option value="" disabled>Select {label}</option>
      {children}
    </select>
  </div>
);

const ValidatedField = ({ label, value, maxLength, onChange, placeholder, hint }) => {
  const isComplete = value.length === maxLength;
  const isPartial = value.length > 0 && value.length < maxLength;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <label className="text-[12px] font-[500] text-gray-500">{label}</label>
        {isPartial && (
          <span className="text-[11px] text-red-400">{maxLength - value.length} digits remaining</span>
        )}
        {isComplete && (
          <span className="text-[11px] text-green-500 font-[500]">✓ Valid {hint}</span>
        )}
      </div>
      <input
        type="text"
        value={value}
        onChange={onChange}
        required
        maxLength={maxLength}
        placeholder={placeholder}
        className={`w-full px-3 py-2 text-[13px] text-gray-800 bg-white border rounded-lg outline-none transition-colors
          hover:border-orange-200 focus:ring-2 focus:ring-orange-100
          ${isComplete
            ? "border-green-400 focus:border-green-400"
            : isPartial
            ? "border-red-300 focus:border-orange-200"
            : "border-gray-200 focus:border-orange-200"
          }`}
      />
    </div>
  );
};

const AddEditStaff = () => {
  const [staffData, setDoctorData] = useState({
    full_name: "",
    role: "",
    date_of_birth: "",
    gender: "",
    contact_number: "",
    email: "",
    password: "",
    address: "",
    qualification: "",
    hire_date: "",
    salary: 0.00,
    cnic: "",
    status: "1",
  });

  const [roleData, setRoleData] = useState([]);
  const [addPortalAccess, setAddPortalAccess] = useState(false);
  const [portalType, setPortalType] = useState("laboratory");

  const portalRoles = roleData.filter((role) =>
    portalType === "radiology"
      ? /radiolog/i.test(role.role_name)
      : /lab/i.test(role.role_name)
  );
  const availablePortals = [
    { value: "laboratory", label: "Laboratory" },
    { value: "radiology", label: "Radiology" },
  ].filter((portal) =>
    portal.value === "radiology" || roleData.some((role) => /lab/i.test(role.role_name))
  );

  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const getRoles = async () => {
      try {
        const response = await roleService.fetchAllRoles();
        setRoleData(response.roles);
      } catch (error) {
        toast.error("Error fetching Roles");
      }
    };
    getRoles();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDoctorData({ ...staffData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const selectedPortalRole = portalRoles.find(
      (role) => String(role.role_id) === String(staffData.role)
    );
    if (addPortalAccess && portalType === "laboratory" && !selectedPortalRole) {
      toast.error("Choose a Laboratory role to enable portal login.");
      return;
    }
    if (addPortalAccess && portalType === "radiology" && portalRoles.length > 0 && !selectedPortalRole) {
      toast.error("Choose a Radiology role to enable portal login.");
      return;
    }

    // If portal access isn't enabled, don't send a password at all —
    // this is a plain staff record with no portal login.
    let payload = addPortalAccess
      ? staffData
      : { ...staffData, password: "" };

    try {
      if (addPortalAccess && portalType === "radiology" && !selectedPortalRole) {
        const created = await roleService.create({
          role_name: "Radiology Staff",
          description: "Staff account for Radiology portal access.",
        });
        let newRoleId = created.role?.insertId;
        let newRole = newRoleId
          ? { role_id: newRoleId, role_name: "Radiology Staff" }
          : null;

        if (!newRoleId) {
          const latestRoles = await roleService.fetchAllRoles();
          newRole = (latestRoles.roles || []).find(
            (role) => /radiolog/i.test(role.role_name)
          );
          newRoleId = newRole?.role_id;
        }

        if (!newRoleId) {
          throw new Error("Could not create a Radiology staff role.");
        }

        setRoleData((roles) =>
          roles.some((role) => String(role.role_id) === String(newRoleId))
            ? roles
            : [...roles, newRole]
        );
        setDoctorData((prev) => ({ ...prev, role: newRoleId }));
        payload = { ...staffData, role: newRoleId };
      }
      if (id) {
        await staffService.update(id, payload);
        toast.success("Staff updated successfully!");
      } else {
        await staffService.create(payload);
        toast.success("Staff added successfully!");
      }
      navigate("/staff");
    } catch (error) {
      toast.error("Error saving staff.");
    }
  };

  return (
    <div className="bg-gray-50 flex items-start justify-center py-6 sm:py-10 px-3 sm:px-4">
      <div className="w-full max-w-7xl">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">

          {/* ── Header ── */}
          <div className="bg-[#215187] px-5 sm:px-8 py-4 sm:py-5">
            <h1 className="text-white text-[16px] sm:text-[18px] font-[500] tracking-tight">
              {id ? "Edit Staff" : "New Staff Registration"}
            </h1>
            <p className="text-gray-400 text-[12px] mt-0.5">
              {id
                ? "Update the staff member's details below"
                : "Fill in the details below to register a new staff member"}
            </p>
          </div>

          {/* ── Form ── */}
          <form onSubmit={handleSubmit} className="px-4 sm:px-6 md:px-8 py-5 sm:py-6">

            {/* Personal Information */}
            <SectionLabel>Personal Information</SectionLabel>

            {/* Row 1 — 1 col mobile, 2 col sm, 3 col lg */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              <InputField
                label="Full Name"
                type="text"
                name="full_name"
                value={staffData.full_name}
                onChange={handleChange}
                required
                placeholder="Enter full name"
              />
              <ValidatedField
                label="Phone Number"
                value={staffData.contact_number}
                maxLength={11}
                placeholder="e.g. 03001234567"
                hint="number"
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "").slice(0, 11);
                  setDoctorData({ ...staffData, contact_number: val });
                }}
              />
              <ValidatedField
                label="CNIC"
                value={staffData.cnic}
                maxLength={13}
                placeholder="13 digits, numbers only"
                hint="CNIC"
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "").slice(0, 13);
                  setDoctorData({ ...staffData, cnic: val });
                }}
              />
            </div>

            {/* Row 2 — 1 col mobile, 2 col sm, 3 col lg */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mt-3 sm:mt-4">
              <InputField
                label="Date of Birth"
                type="date"
                name="date_of_birth"
                value={staffData.date_of_birth}
                onChange={handleChange}
                required
              />
              <InputField
                label="Email"
                type="email"
                name="email"
                value={staffData.email}
                onChange={handleChange}
                required
                placeholder="Enter email address"
              />
              <InputField
                label="Qualification"
                type="text"
                name="qualification"
                value={staffData.qualification}
                onChange={handleChange}
                required
                placeholder="Enter qualification"
              />
            </div>

            {/* Portal access toggle */}
            <div className="mt-4 sm:mt-5 flex items-center justify-between gap-3 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <div className="flex flex-col">
                <span className="text-[13px] font-[500] text-gray-700">
                  Enable Portal Login
                </span>
                <span className="text-[11px] text-gray-400">
                  Choose whether this staff member signs in to Laboratory or Radiology.
                </span>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={addPortalAccess}
                onClick={() => {
                  const next = !addPortalAccess;
                  setAddPortalAccess(next);

                  if (next) {
                    // Match the selected portal to a compatible staff role.
                    const portalRole = roleData.find((role) =>
                      portalType === "radiology"
                        ? /radiolog/i.test(role.role_name)
                        : /lab/i.test(role.role_name)
                    );
                    if (portalRole) {
                      setDoctorData((prev) => ({ ...prev, role: portalRole.role_id }));
                    }
                  } else {
                    // Clear any typed password if the user turns portal access back off
                    setDoctorData((prev) => ({ ...prev, password: "" }));
                  }
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0
                  ${addPortalAccess ? "bg-[#2F6DB4]" : "bg-gray-300"}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                    ${addPortalAccess ? "translate-x-6" : "translate-x-1"}`}
                />
              </button>
            </div>

            {addPortalAccess && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-3 sm:mt-4">
                <SelectField
                  label="Portal Access"
                  name="portalType"
                  value={portalType}
                  onChange={(e) => {
                    const nextPortalType = e.target.value;
                    setPortalType(nextPortalType);
                    const matchingRole = roleData.find((role) =>
                      nextPortalType === "radiology"
                        ? /radiolog/i.test(role.role_name)
                        : /lab/i.test(role.role_name)
                    );
                    if (matchingRole) {
                      setDoctorData((prev) => ({ ...prev, role: matchingRole.role_id }));
                    }
                  }}
                  required
                >
                  {availablePortals.map((portal) => (
                    <option key={portal.value} value={portal.value}>
                      {portal.label} Portal
                    </option>
                  ))}
                </SelectField>
                <InputField
                  label={`${portalType === "radiology" ? "Radiology" : "Laboratory"} Portal Password`}
                  type="password"
                  name="password"
                  value={staffData.password}
                  onChange={handleChange}
                  required={addPortalAccess}
                  placeholder="Set a password for portal login"
                  hint="Staff will use their email and this password to sign in"
                />
              </div>
            )}

            {/* Employment Details */}
            <SectionLabel>Employment Details</SectionLabel>

            {/* Row 3 — 1 col mobile, 2 col sm+ */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <InputField
                label="Address"
                type="text"
                name="address"
                value={staffData.address}
                onChange={handleChange}
                required
                placeholder="Enter residential address"
              />
              <InputField
                label="Salary (Pay)"
                type="number"
                name="salary"
                value={staffData.salary}
                onChange={handleChange}
                required
                placeholder="Enter salary amount"
              />
            </div>

            {/* Row 4 — 1 col mobile, 2 col sm, 3 col lg */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mt-3 sm:mt-4">
              <InputField
                label="Date of Appointment"
                type="date"
                name="hire_date"
                value={staffData.hire_date}
                onChange={handleChange}
                required
              />
              {addPortalAccess && portalType === "radiology" && portalRoles.length === 0 ? (
                <div className="flex flex-col gap-1">
                  <label className="text-[12px] font-[500] text-gray-500">Radiology Role</label>
                  <div className="w-full px-3 py-2 text-[13px] text-gray-600 bg-gray-50 border border-gray-200 rounded-lg">
                    Radiology Staff role will be created when saved
                  </div>
                </div>
              ) : (
                <SelectField
                  label={addPortalAccess ? `${portalType === "radiology" ? "Radiology" : "Laboratory"} Role` : "Assigned Role"}
                  name="role"
                  value={staffData.role}
                  onChange={handleChange}
                  required
                >
                  {(addPortalAccess ? portalRoles : roleData).map((r) => (
                    <option key={r.role_id} value={r.role_id}>
                      {r.role_name}
                    </option>
                  ))}
                </SelectField>
              )}
              <SelectField
                label="Gender"
                name="gender"
                value={staffData.gender}
                onChange={handleChange}
                required
              >
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </SelectField>
            </div>

            {/* ── Footer ── */}
            <div className="border-t border-gray-100 mt-7 sm:mt-8 pt-5 sm:pt-6 flex flex-col-reverse sm:flex-row items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="w-full sm:w-auto text-[13px] text-gray-400 hover:text-gray-600 transition-colors font-[500] text-center sm:text-left"
              >
                ← Cancel
              </button>
              <button
                type="submit"
                className="w-full sm:w-auto bg-[#2F6DB4] hover:bg-[#255A8C] text-white border border-gray-100 transition-colors text-[13px] font-[500] px-6 py-2.5 rounded-lg"
              >
                {id ? "Update Staff" : "Save & Continue →"}
              </button>
            </div>

          </form>
        </div>

        <p className="text-center text-[12px] text-gray-400 mt-4 px-2">
          {id
            ? "Changes will be saved immediately."
            : "Staff member will be available for assignment after saving."}
        </p>
      </div>
    </div>
  );
};

export default AddEditStaff;